import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterOption } from 'models/Filters';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import api from 'api';
import ListingContext from '../../../../providers/Listing/Listing.context';
import { LEAD_STAGES, LEAD_STAGES_COPY, LeadStages } from 'models/Lead';
import { NDA_COPIES_WITHOUT_NAME, NDA_STATUSES } from 'models/Nda';
import { TIMEFRAME } from 'dateUtils';
import useMultiSort from 'hooks/useMultiSort';
import {
  HEADING_TRANSLATIONS,
  MULTI_SORT_DIRECTION_COPY,
  SORTING_VALUES,
} from '../../data';
import { sortOptions } from 'utils';

const STAGE_LABELS: Record<LeadStages, string> = {
  [LEAD_STAGES.ENQUIRE]: LEAD_STAGES_COPY.ENQUIRY,
  [LEAD_STAGES.ACCEPT]: LEAD_STAGES_COPY.ACCEPTED,
  [LEAD_STAGES.CONFIRM]: LEAD_STAGES_COPY.PROSPECT,
  [LEAD_STAGES.OFFER]: LEAD_STAGES_COPY.BIDDER,
};

export default function useLeadsFilterOptions(
  paramPrefix = '',
  hasStageFilter = true,
) {
  const { t } = useTranslation();

  const { getMultiSelectSearchableFilter } = useGetFilterOptions();

  const createSelectOptions = useCreateSelectOptions();

  const {
    dealRoom: { bossModeReferrerOptions },
    enquiry,
  } = useContext(ListingContext);

  const getParam = useCallback(
    (field: string) =>
      paramPrefix
        ? `${paramPrefix}${field[0].toUpperCase()}${field.slice(1)}`
        : field,
    [paramPrefix],
  );

  const nameParam = getParam('name');
  const emailParam = getParam('email');
  const companyParam = getParam('company');
  const stageParam = getParam('buyerStep');
  const ndaStatusParam = getParam('signingStatus');
  const createdTimeframeParam = getParam('createdTimeframe');
  const lastSeenTimeframeParam = getParam('lastSeenTimeframe');
  const referrerParam = getParam('referrerName');

  const { multiSortDirectionTranslation } = useMultiSort(
    SORT_COLUMNS,
    SORTING_VALUES,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const getNameOptions = useCallback(
    async (search: string) => {
      const { data } = await api.portfolio.seller.getLeadNameOptions(
        enquiry?._id,
        search,
      );

      return sortOptions(data);
    },
    [enquiry?._id],
  );

  const getEmailOptions = useCallback(
    async (search: string) => {
      const { data } = await api.portfolio.seller.getLeadEmailOptions(
        enquiry?._id,
        search,
      );

      return sortOptions(data);
    },
    [enquiry?._id],
  );

  const getCompanyOptions = useCallback(
    async (search: string) => {
      const { data } = await api.portfolio.seller.getLeadCompanyOptions(
        enquiry?._id,
        search,
      );

      return sortOptions(data);
    },
    [enquiry?._id],
  );

  const nameFilter = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('name'),
        nameParam,
        nameParam,
        getNameOptions,
      ),
    [getNameOptions, getMultiSelectSearchableFilter, nameParam, t],
  );

  const emailFilter = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('email'),
        emailParam,
        emailParam,
        getEmailOptions,
      ),
    [emailParam, getEmailOptions, getMultiSelectSearchableFilter, t],
  );

  const companyFilter = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('company'),
        companyParam,
        companyParam,
        getCompanyOptions,
      ),
    [companyParam, getCompanyOptions, getMultiSelectSearchableFilter, t],
  );

  const referrerFilter = useMemo(
    () => ({
      value: referrerParam,
      label: t('referrer'),
      options: createSelectOptions(bossModeReferrerOptions),
    }),
    [bossModeReferrerOptions, createSelectOptions, referrerParam, t],
  );

  const stageFilter = useMemo(
    () => ({
      value: stageParam,
      label: t('stage'),
      options: Object.values(LEAD_STAGES).map((value) => ({
        value,
        label: t(STAGE_LABELS[value]),
      })),
    }),
    [stageParam, t],
  );

  const ndaStatusFilter = useMemo(
    () => ({
      value: ndaStatusParam,
      label: t('ndaStatus'),
      options: Object.values(NDA_STATUSES).map((value) => ({
        value,
        label: t(NDA_COPIES_WITHOUT_NAME[value]),
      })),
    }),
    [ndaStatusParam, t],
  );

  const addedFilter = useMemo(
    () => ({
      value: createdTimeframeParam,
      label: t('added'),
      shouldSortOptions: false,
      options: createSelectOptions(Object.values(TIMEFRAME)),
    }),
    [createSelectOptions, createdTimeframeParam, t],
  );

  const lastSeenFilter = useMemo(
    () => ({
      value: lastSeenTimeframeParam,
      label: t('lastSeen'),
      shouldSortOptions: false,
      options: createSelectOptions(Object.values(TIMEFRAME)),
    }),
    [createSelectOptions, lastSeenTimeframeParam, t],
  );

  const filterOptions = useMemo(
    () =>
      [
        nameFilter,
        emailFilter,
        companyFilter,
        ...(hasStageFilter ? [stageFilter] : []),
        ndaStatusFilter,
        addedFilter,
        lastSeenFilter,
        referrerFilter,
      ] as FilterOption[],
    [
      addedFilter,
      companyFilter,
      emailFilter,
      hasStageFilter,
      lastSeenFilter,
      nameFilter,
      ndaStatusFilter,
      referrerFilter,
      stageFilter,
    ],
  );

  const columnFilterOptions = useMemo(
    () => ({
      name: { options: nameFilter as FilterOption },
      email: { options: emailFilter as FilterOption },
      company: { options: companyFilter as FilterOption },
      stage: {
        ...(hasStageFilter && { options: stageFilter }),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.stage],
      },
      nda: {
        options: ndaStatusFilter,
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.nda],
      },
      added: {
        options: addedFilter,
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.added],
      },
      lastSeen: {
        options: lastSeenFilter,
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.lastSeen],
      },
      referrer: { options: referrerFilter },
      chat: {
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.chat],
      },
      notes: {
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.notes],
      },
      phone: {
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.phone],
      },
    }),
    [
      addedFilter,
      companyFilter,
      emailFilter,
      hasStageFilter,
      lastSeenFilter,
      multiSortDirectionTranslation,
      nameFilter,
      ndaStatusFilter,
      referrerFilter,
      stageFilter,
    ],
  );

  return { filterOptions, columnFilterOptions };
}

const SORT_COLUMNS = Object.keys(SORTING_VALUES);
